'use client'

import Image from 'next/image'
import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const ICON_PATH = 'M428.09 175.63C426.234 173.773 423.717 172.726 421.09 172.72H318.79L391.14 100.37C392.065 99.4469 392.799 98.3504 393.3 97.1432C393.8 95.9361 394.058 94.6421 394.058 93.3352C394.058 92.0284 393.8 90.7343 393.3 89.5272C392.799 88.32 392.065 87.2234 391.14 86.3002L344.69 39.8502C343.767 38.9253 342.67 38.1915 341.463 37.6908C340.256 37.1902 338.962 36.9325 337.655 36.9325C336.348 36.9325 335.054 37.1902 333.847 37.6908C332.64 38.1915 331.543 38.9253 330.62 39.8502L258.27 112.2V9.9302C258.269 8.62064 258.009 7.32422 257.505 6.11548C257.001 4.90673 256.263 3.80946 255.334 2.88672C254.405 1.96397 253.302 1.23395 252.09 0.738584C250.878 0.243219 249.58 -0.00772431 248.27 0.00018113H182.59C181.285 -0.00113275 179.992 0.25467 178.786 0.752989C177.579 1.25131 176.483 1.98239 175.559 2.90448C174.635 3.82656 173.901 4.9216 173.401 6.12707C172.9 7.33255 172.642 8.62486 172.64 9.9302V112.11L100.37 39.8502C99.4478 38.9256 98.3521 38.192 97.1458 37.6915C95.9394 37.1909 94.6462 36.9333 93.3402 36.9333C92.0341 36.9333 90.741 37.1909 89.5346 37.6915C88.3283 38.192 87.2326 38.9256 86.3102 39.8502L39.8602 86.2902C38.9329 87.2121 38.197 88.3082 37.6948 89.5156C37.1927 90.7229 36.9341 92.0176 36.9341 93.3252C36.9341 94.6328 37.1927 95.9275 37.6948 97.1348C38.197 98.3422 38.9329 99.4383 39.8602 100.36L112.21 172.71H9.95019C8.63895 172.709 7.34039 172.967 6.12933 173.47C4.91827 173.972 3.81863 174.71 2.89377 175.639C1.9689 176.569 1.23709 177.672 0.740477 178.886C0.243864 180.099 -0.00773452 181.399 0.000181133 182.71C-0.0024715 202.78 7.96701 222.029 22.1557 236.223C36.3444 250.418 55.5903 258.395 75.6602 258.4H145.66C157.607 258.4 169.427 260.846 180.393 265.588C191.359 270.329 201.237 277.266 209.42 285.97C210.192 286.808 211.129 287.476 212.172 287.934C213.215 288.391 214.341 288.627 215.48 288.627C216.619 288.627 217.746 288.391 218.789 287.934C219.832 287.476 220.769 286.808 221.54 285.97C229.719 277.266 239.594 270.329 250.556 265.587C261.519 260.846 273.336 258.4 285.28 258.4H355.33C375.4 258.395 394.646 250.418 408.835 236.223C423.023 222.029 430.993 202.78 430.99 182.71C431 181.397 430.748 180.095 430.25 178.879C429.752 177.664 429.018 176.56 428.09 175.63ZM245.84 209.18C238.826 216.185 229.601 220.541 219.735 221.507C209.869 222.473 199.974 219.988 191.735 214.477C183.496 208.965 177.422 200.767 174.549 191.28C171.676 181.793 172.182 171.603 175.979 162.446C179.776 153.29 186.631 145.733 195.375 141.063C204.119 136.394 214.212 134.9 223.933 136.837C233.655 138.774 242.404 144.022 248.691 151.686C254.977 159.351 258.412 168.957 258.41 178.87C258.41 184.501 257.299 190.076 255.142 195.276C252.985 200.477 249.824 205.202 245.84 209.18Z'

const INDUSTRIES = [
  { label: 'Healthcare & Education', href: '/industries/healthcare-education' },
]

interface NavbarProps {
  forceDark?: boolean
}

/**
 * Convert a `#rrggbb` (or `#rgb`) hex string into an `rgba(...)` value at
 * the given alpha. Used to render the navbar background at 75% of the
 * underlying section's declared color. Returns `null` for unparseable
 * inputs so the navbar can fall back to its default translucent style.
 */
function hexToRgba(hex: string | undefined, alpha: number): string | null {
  if (!hex) return null
  const cleaned = hex.trim().replace('#', '')
  const isShort = cleaned.length === 3
  const isLong = cleaned.length === 6
  if (!isShort && !isLong) return null
  const r = parseInt(isShort ? cleaned[0] + cleaned[0] : cleaned.slice(0, 2), 16)
  const g = parseInt(isShort ? cleaned[1] + cleaned[1] : cleaned.slice(2, 4), 16)
  const b = parseInt(isShort ? cleaned[2] + cleaned[2] : cleaned.slice(4, 6), 16)
  if ([r, g, b].some(Number.isNaN)) return null
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export default function Navbar({ forceDark = false }: NavbarProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [navDark, setNavDark] = useState(forceDark)
  // Current section background color (raw hex from `data-bg`). Null when
  // the navbar isn't sitting over any `[data-bg]` section.
  const [navBg, setNavBg] = useState<string | null>(null)
  const [industriesOpen, setIndustriesOpen] = useState(false)
  const [mobileIndustriesOpen, setMobileIndustriesOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const navRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const triggers: ScrollTrigger[] = []

    // Dark / light text styling, driven by `data-nav-theme="dark"`.
    if (!forceDark) {
      const darkSections = document.querySelectorAll<HTMLElement>('[data-nav-theme="dark"]')
      darkSections.forEach((section) => {
        triggers.push(
          ScrollTrigger.create({
            trigger: section,
            start: 'top 48px',
            end: 'bottom 48px',
            onEnter: () => setNavDark(true),
            onLeave: () => setNavDark(false),
            onEnterBack: () => setNavDark(true),
            onLeaveBack: () => setNavDark(false),
          })
        )
      })
    }

    // Background tint, driven by `data-bg="#rrggbb"`. We only set on
    // enter/enterBack so that as one section yields to the next, the new
    // color overwrites the old; leaving a section never clears the value
    // (the next entering one will). Prevents a flash to the default at
    // section seams under the navbar line.
    const bgSections = document.querySelectorAll<HTMLElement>('[data-bg]')
    bgSections.forEach((section) => {
      const bg = section.dataset.bg
      if (!bg) return
      triggers.push(
        ScrollTrigger.create({
          trigger: section,
          start: 'top 48px',
          end: 'bottom 48px',
          onEnter: () => setNavBg(bg),
          onEnterBack: () => setNavBg(bg),
        })
      )
    })

    return () => { triggers.forEach((t) => t.kill()) }
  }, [forceDark])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIndustriesOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const navLinks = ['Services', 'Work', 'About', 'Blog', 'Contact']

  return (
    <>
      <nav
        ref={navRef}
        className="fixed top-0 left-0 right-0 z-50 px-6 md:px-16 pt-4"
        aria-label="Main navigation"
      >
        <div
          className={`font-display font-medium mx-auto max-w-[1600px] flex items-center justify-between px-5 py-3 rounded-[10px] backdrop-blur-md border transition-colors duration-500 ${
            navDark
              ? `${navBg ? '' : 'bg-white/[0.042]'} border-white/10 text-white`
              : `${navBg ? '' : 'bg-charcoal-900/[0.028]'} border-charcoal-900/[0.06] text-charcoal-900`
          }`}
          style={navBg ? { backgroundColor: hexToRgba(navBg, 0.75) ?? undefined } : undefined}
        >
          <a href="/" className="after:hidden flex items-center gap-2.5 flex-shrink-0 transition-none !duration-0">
            <svg viewBox="0 0 431 289" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
              <path d={ICON_PATH} fill="currentColor" />
            </svg>
            <span className="text-[0.95rem] tracking-tight font-medium">Soleno</span>
          </a>

          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((item) => (
              <a
                key={item}
                href={
                  item === 'Blog'
                    ? '/blog'
                    : item === 'Contact'
                    ? '/contact'
                    : item === 'Work'
                    ? '/work'
                    : item === 'About'
                    ? '/about'
                    : `/#${item.toLowerCase()}`
                }
                className={`px-4 py-1.5 rounded-lg text-sm tracking-wide after:hidden transition-colors ${
                  navDark
                    ? 'text-white/60 hover:text-white hover:bg-white/[0.08]'
                    : 'text-charcoal-500 hover:text-charcoal-900 hover:bg-charcoal-900/[0.05]'
                }`}
              >
                {item}
              </a>
            ))}

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setIndustriesOpen(!industriesOpen)}
                className={`flex items-center gap-1 px-4 py-1.5 rounded-lg text-sm tracking-wide transition-colors ${
                  navDark
                    ? 'text-white/60 hover:text-white hover:bg-white/[0.08]'
                    : 'text-charcoal-500 hover:text-charcoal-900 hover:bg-charcoal-900/[0.05]'
                }`}
              >
                Industries
                <svg
                  className={`w-3.5 h-3.5 transition-transform duration-200 ${industriesOpen ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <AnimatePresence>
                {industriesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 6, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 6, scale: 0.97 }}
                    transition={{ duration: 0.15 }}
                    className={`absolute right-0 top-full mt-2 w-56 rounded-xl border p-1.5 backdrop-blur-xl shadow-lg ${
                      navDark
                        ? 'bg-charcoal-800/90 border-white/10'
                        : 'bg-white/90 border-charcoal-100'
                    }`}
                  >
                    {INDUSTRIES.map((industry) => (
                      <a
                        key={industry.href}
                        href={industry.href}
                        onClick={() => setIndustriesOpen(false)}
                        className={`block px-3.5 py-2.5 rounded-lg text-sm tracking-wide after:hidden transition-colors ${
                          navDark
                            ? 'text-white/70 hover:text-white hover:bg-white/[0.08]'
                            : 'text-charcoal-600 hover:text-charcoal-900 hover:bg-charcoal-50'
                        }`}
                      >
                        {industry.label}
                      </a>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <a
              href="https://calendly.com/shahidhasan/product-design"
              target="_blank"
              rel="noopener noreferrer"
              className={`hidden md:inline-flex items-center gap-2 pl-1.5 pr-4 py-1.5 ml-4 rounded-[8px] text-sm tracking-wide after:hidden transition-colors duration-300 ${
                navDark
                  ? 'bg-white text-charcoal-900 hover:bg-white/90'
                  : 'bg-brand-500 text-white hover:bg-brand-600'
              }`}
            >
              <Image
                src="/images/linkedin-profile.webp"
                alt=""
                aria-hidden="true"
                width={28}
                height={28}
                className={`w-7 h-7 rounded-[4px] object-cover border-2 ${
                  navDark ? 'border-brand-500' : 'border-white'
                }`}
                draggable={false}
              />
              Book a call
            </a>
          </div>

          <button
            className="md:hidden p-1"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle menu"
          >
            <AnimatePresence mode="wait">
              {isMenuOpen ? (
                <motion.svg
                  key="close" width="18" height="18" viewBox="0 0 18 18" fill="none"
                  initial={{ scale: 0, rotate: -90 }} animate={{ scale: 1, rotate: 0 }} exit={{ scale: 0, rotate: 90 }}
                  transition={{ duration: 0.25 }}
                >
                  <path d="M4 4L14 14M14 4L4 14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </motion.svg>
              ) : (
                <motion.svg
                  key="menu" width="18" height="18" viewBox="0 0 18 18" fill="none"
                  initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
                  transition={{ duration: 0.25 }}
                >
                  <path d="M3 6h12M3 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </motion.svg>
              )}
            </AnimatePresence>
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="font-display font-medium fixed inset-0 z-40 bg-charcoal-900/95 backdrop-blur-xl flex flex-col items-center justify-center gap-8"
          >
            {navLinks.map((item, index) => (
              <motion.a
                key={item}
                href={
                  item === 'Blog'
                    ? '/blog'
                    : item === 'Contact'
                    ? '/contact'
                    : item === 'Work'
                    ? '/work'
                    : item === 'About'
                    ? '/about'
                    : `/#${item.toLowerCase()}`
                }
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3, delay: index * 0.05 }}
                className="text-3xl text-white/90 hover:text-white tracking-wide after:hidden transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                {item}
              </motion.a>
            ))}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, delay: navLinks.length * 0.05 }}
              className="flex flex-col items-center"
            >
              <button
                onClick={() => setMobileIndustriesOpen(!mobileIndustriesOpen)}
                className="flex items-center gap-2 text-3xl text-white/90 hover:text-white tracking-wide after:hidden transition-colors"
              >
                Industries
                <svg
                  className={`w-5 h-5 transition-transform duration-200 ${mobileIndustriesOpen ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              <AnimatePresence>
                {mobileIndustriesOpen && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden mt-4 flex flex-col items-center gap-3"
                  >
                    {INDUSTRIES.map((industry) => (
                      <a
                        key={industry.href}
                        href={industry.href}
                        className="text-xl text-white/50 hover:text-white tracking-wide after:hidden transition-colors"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {industry.label}
                      </a>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Mobile CTA — mirrors the desktop "Book a call" button. */}
            <motion.a
              href="https://calendly.com/shahidhasan/product-design"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsMenuOpen(false)}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.3, delay: (navLinks.length + 1) * 0.05 }}
              className="mt-4 inline-flex items-center gap-2.5 pl-2 pr-5 py-2 rounded-[10px] bg-white text-charcoal-900 text-base tracking-wide after:hidden hover:bg-white/90 transition-colors"
            >
              <Image
                src="/images/linkedin-profile.webp"
                alt=""
                aria-hidden="true"
                width={32}
                height={32}
                className="w-8 h-8 rounded-[4px] object-cover border-2 border-brand-500"
                draggable={false}
              />
              Book a call
            </motion.a>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
